/**
 * 发品 / 模板币种来源分流：
 * - 半托管 / 全托管：商品发布规范 【currency】 → cost_info.currency
 * - 自运营：query-site-list 自营站点币种 → price_info_list.currency
 */
import { extractPublishCurrencies, pickPublishCurrency } from '@/shein-platform/utils/sheinPublishFillInStandard';
import { sheinShopCapabilities } from '@/shein-platform/utils/sheinShopCapabilities';
import {
  extractSiteListCurrencies,
  pickSiteListCurrency,
} from '@/shein-platform/utils/sheinSiteList';

export const FILL_IN_CURRENCY_EMPTY_WARNING = '未从商品发布规范解析到可用币种，供货价可能无法提交';
export const SITE_LIST_CURRENCY_EMPTY_WARNING = '未从店铺站点列表解析到可用币种，售价可能无法提交';

function resolveCaps(shopCaps, shopType) {
  if (shopCaps && typeof shopCaps === 'object' && 'usesPriceInfo' in shopCaps) {
    return shopCaps;
  }
  return sheinShopCapabilities(shopType);
}

export function usesSiteListCurrency(shopCapsOrType) {
  return Boolean(resolveCaps(shopCapsOrType, shopCapsOrType)?.usesPriceInfo);
}

/**
 * @returns {{
 *   source: 'site-list' | 'fill-in-standard',
 *   currencies: { code: string, name: string }[],
 *   code: string,
 *   emptyWarning: string,
 * }}
 */
export function resolveShopPublishCurrencies({
  shopType = '',
  shopCaps = null,
  siteList = null,
  fillInStandard = null,
  preferred = '',
  selectedSites = [],
} = {}) {
  const caps = resolveCaps(shopCaps, shopType);
  if (caps?.usesPriceInfo) {
    const currencies = extractSiteListCurrencies(siteList);
    return {
      source: 'site-list',
      currencies,
      code: pickSiteListCurrency(siteList, { preferred, selectedSites }),
      emptyWarning: currencies.length ? '' : SITE_LIST_CURRENCY_EMPTY_WARNING,
    };
  }
  const currencies = extractPublishCurrencies(fillInStandard);
  return {
    source: 'fill-in-standard',
    currencies,
    code: pickPublishCurrency(currencies, preferred),
    emptyWarning: currencies.length ? '' : FILL_IN_CURRENCY_EMPTY_WARNING,
  };
}

/**
 * 提交前校验表单币种是否落在当前店铺合法来源里。
 * 半托管/全托管：规范未返回 currency 时不拦（与历史预检一致）。
 * 自运营：站点列表无币种则失败。
 */
export function validateShopPublishCurrency(form = {}, {
  shopType = '',
  shopCaps = null,
  siteList = null,
  fillInStandard = null,
  standardRaw = null,
} = {}) {
  const resolved = resolveShopPublishCurrencies({
    shopType: shopType || form?.shopType,
    shopCaps,
    siteList: siteList || form?.siteList,
    fillInStandard: fillInStandard || standardRaw,
    preferred: form?.currency,
    selectedSites: form?.selectedSites,
  });
  const cur = String(form?.currency || '').trim();
  if (resolved.source === 'site-list') {
    if (!resolved.currencies.length) {
      return {
        ok: false,
        message: resolved.emptyWarning || SITE_LIST_CURRENCY_EMPTY_WARNING,
        section: 'sales',
        field: 'currency',
        ...resolved,
      };
    }
    if (cur && !resolved.currencies.some((row) => String(row.code).toUpperCase() === cur.toUpperCase())) {
      return {
        ok: false,
        message: `售价币种「${cur}」不在店铺可售站点币种中（可用：${resolved.currencies.map((row) => row.code).join('、')}）`,
        section: 'sales',
        field: 'currency',
        ...resolved,
      };
    }
    return { ok: true, ...resolved };
  }
  if (resolved.currencies.length && cur
    && !resolved.currencies.some((row) => String(row.code).toUpperCase() === cur.toUpperCase())) {
    return {
      ok: false,
      message: `供货价币种「${cur}」不在商品发布规范允许列表中（可用：${resolved.currencies.map((row) => row.code).join('、')}）`,
      section: 'sales',
      field: 'currency',
      ...resolved,
    };
  }
  return { ok: true, ...resolved };
}

/**
 * 自运营站点快照无币种时再拉一次平台站点列表。
 * @param {{ refreshSiteList?: () => Promise<{ siteList?: object } | object> }} opts
 */
export async function loadShopPublishCurrencies({
  shopType = '',
  shopCaps = null,
  siteList = null,
  fillInStandard = null,
  preferred = '',
  selectedSites = [],
  refreshSiteList,
} = {}) {
  let resolvedSiteList = siteList ?? null;
  const caps = resolveCaps(shopCaps, shopType);
  if (
    caps?.usesPriceInfo
    && !extractSiteListCurrencies(resolvedSiteList).length
    && typeof refreshSiteList === 'function'
  ) {
    try {
      const refreshed = await refreshSiteList();
      if (refreshed != null) {
        resolvedSiteList = refreshed.siteList ?? refreshed;
      }
    } catch {
      // 保留快照，由 emptyWarning 提示
    }
  }
  return {
    siteList: resolvedSiteList,
    ...resolveShopPublishCurrencies({
      shopCaps: caps,
      shopType,
      siteList: resolvedSiteList,
      fillInStandard,
      preferred,
      selectedSites,
    }),
  };
}
